import {
  Typography,
  TextField,
  Box,
  Tabs,
  Tab,
  Button,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Chip,
} from "@mui/material";
import { TreeView, TreeItem } from "@mui/lab";
import { CheckCircle } from "@mui/icons-material";

import JsonEditorAce from "../../../components/code/ace/json.editor";
import TemplateEditorAce from "../../../components/code/ace/template.editor";
import { useEffect, useState } from "react";
import AttributesInput from "../../../components/attributes/attributes.input";
import { useDataProvider } from "react-admin";
import { ChevronRight, ExpandMore } from "@mui/icons-material";
import { useAppDataContext } from "../../../framework/app/app.data.context";

const TabPanel = ({
  children,
  value,
  index,
}: {
  children: React.ReactNode;
  value: number;
  index: number;
}) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const AIFunctionEdit: React.FC<{
  item: any;
  onChange: (updatedItem: any) => void;
}> = ({ item, onChange }) => {
  const [updatedItem, setUpdatedItem] = useState(item);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedNode, setSelectedNode] = useState<string>(""); // never undefined
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
  const [topLevelTab, setTopLevelTab] = useState(0); // 0: OpenAPI, 1: Source
  const [sourceTab, setSourceTab] = useState(0); // 0: Integration, 1: Webhook, 2: Mock, 3: SQL, 4: App Configuration, 5: TypeScript


  const [integrationOptions, setIntegrationOptions] = useState<
    {
      functions: any;
      integrationId: string;
      name: string;
    }[]
  >([]);
  
  const [sqlConnections, setSqlConnections] = useState<
    {
      id: string;
      key: string;
      name: string;
      providerKey: string;
    }[]
  >([]);

  const dataProvider = useDataProvider();
  const apiContext = useAppDataContext();

  // Fetch options for the file selector using the dataProvider
  useEffect(() => {
    const fetchIntegrationOptions = async () => {
      try {
        const { data } = await dataProvider.getList("integration", {
          filter: {}, // Your filter logic
          pagination: { page: 1, perPage: 50 }, // Adjust pagination if needed
          sort: { field: "name", order: "ASC" }, // Sort options
        });
        setIntegrationOptions(
          data.map((integration: any) => ({
            integrationId: integration.integrationId,
            name: integration.name,
            webhookUrl: "", // Provide a default empty value
            functions: integration.functions.map((func: any) => ({
              id: func.id,
              name: func.name,
              webhookUrl: func.webhookUrl,
            })),
          }))
        );
      } catch (error) {
        console.error("Failed to fetch integration options:", error);
      }
    };
    
    const fetchSQLConnections = async () => {
      try {
        // Use apiContext to get correct URL and auth token
        const apiUrl = apiContext?.apiUrl || 'http://localhost:5004';
        const token = await apiContext?.appContext?.tokenProvider?.getIdToken();
        
        const response = await fetch(`${apiUrl}/connection-dp?filter={}&range=[0,99]&sort=["name","ASC"]`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch connections: ${response.statusText}`);
        }
        
        const responseData = await response.json();
        const connections = responseData?.result || [];
        
        // Filter to SQL-type connections (Snowflake, etc.)
        // Note: Field is 'connectionProviderKey' not 'providerKey'
        const sqlConns = connections.filter((conn: any) => 
          conn.connectionProviderKey === 'snowflake' || conn.category === 'database'
        );
        
        setSqlConnections(
          sqlConns.map((conn: any) => ({
            id: conn.id,
            key: conn.key || conn.id,
            name: conn.name,
            providerKey: conn.connectionProviderKey
          }))
        );
      } catch (error) {
        console.error("Failed to fetch SQL connections:", error);
      }
    };

    fetchIntegrationOptions();
    
    // Only fetch SQL connections if apiContext is available
    if (apiContext?.apiUrl && apiContext?.appContext?.tokenProvider) {
      fetchSQLConnections();
    }
  }, [dataProvider, apiContext?.apiUrl]);

  useEffect(() => {
    setSelectedNode(item?.integrationConfig?.integrationFunctionId || "");
    setExpandedNodes(item?.integrationConfig?.integrationId ? [item.integrationConfig.integrationId] : []);
    
    // Auto-detect and navigate to the correct implementation tab
    const detectImplementationType = () => {
      // Check new implementationConfig structure first
      if (item?.implementationConfig?.implementationProviderKey) {
        const providerKey = item.implementationConfig.implementationProviderKey;
        setTopLevelTab(1); // Switch to Implementation tab
        
        switch (providerKey) {
          case 'webhook':
            setSourceTab(1);
            break;
          case 'mock':
            setSourceTab(2);
            break;
          case 'sql':
            setSourceTab(3);
            break;
          case 'typescript':
            setSourceTab(5);
            break;
          case 'integration':
            setSourceTab(0);
            break;
          default:
            // Unknown provider, stay on current tab
            break;
        }
      }
      // Fallback to legacy executionProviderKey
      else if (item?.executionProviderKey) {
        setTopLevelTab(1); // Switch to Implementation tab
        
        switch (item.executionProviderKey) {
          case 'integration':
            setSourceTab(0);
            break;
          case 'webhook':
            setSourceTab(1);
            break;
          case 'mock':
            setSourceTab(2);
            break;
          case 'sql':
            setSourceTab(3);
            break;
          case 'appConfiguration':
            setSourceTab(4);
            break;
          case 'typescript':
            setSourceTab(5);
            break;
          default:
            // No implementation, stay on OpenAPI tab
            setTopLevelTab(0);
            break;
        }
      }
      // Check legacy integrationConfig
      else if (item?.integrationConfig?.integrationFunctionId) {
        setTopLevelTab(1); // Switch to Implementation tab
        setSourceTab(0); // Integration tab
      }
      // No implementation detected - stay on OpenAPI tab
      else {
        setTopLevelTab(0);
      }
    };
    
    detectImplementationType();
  }, [item]);

  // Helper: Determine which implementation type is active
  const getActiveImplementationType = (): string | null => {
    if (updatedItem?.implementationConfig?.implementationProviderKey) {
      return updatedItem.implementationConfig.implementationProviderKey;
    }
    if (updatedItem?.executionProviderKey) {
      return updatedItem.executionProviderKey;
    }
    if (updatedItem?.integrationConfig?.integrationFunctionId) {
      return 'integration';
    }
    return null;
  };
  
  // Helper: Check if a specific implementation type is configured
  const isImplementationConfigured = (type: string): boolean => {
    const activeType = getActiveImplementationType();
    return activeType === type;
  };

  // Check if item is not available and return a fallback UI
  if (!item) {
    return <div>No function data available</div>;
  }

  const handleNodeToggle = (event: React.SyntheticEvent, nodeIds: string[]) => {
    setExpandedNodes(nodeIds);
  };

  // Function to handle changes in the form
  const handleChange = (field: string, value: any) => {
    const newItem = { ...updatedItem, [field]: value };
    newItem.updatedAt = new Date().toISOString();
    setUpdatedItem(newItem);
    onChange(newItem); // Call onChange to update the parent component
  };

  const handleClear = () => {
    setUpdatedItem({
      ...updatedItem,
      integrationConfig: null,
      webhookUrl: "",
      executionProviderKey: "",
    });
    onChange({
      ...updatedItem,
      integrationConfig: null,
      webhookUrl: "",
      executionProviderKey: "",
    });
  };

  const handleFunctionSelect = (functionItem: any) => {
    console.log("functionItem", functionItem);

    const newItem = {
      ...updatedItem,
      integrationConfig: {
        integrationId: functionItem.integrationId,
        integrationFunctionId: functionItem.integrationFunctionId,
      },
      webhookUrl: functionItem.webhookUrl,
    };
    console.log("newItem", newItem);

    setUpdatedItem(newItem);
    onChange(newItem);
  };

  /*
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };
  */
  const handleTopLevelTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTopLevelTab(newValue);
  };

  // Helper: Get SQL from implementationConfig
  const getSQLValue = (item: any): string => {
    if (item.implementationConfig?.implementationProviderKey === 'sql') {
      return item.implementationConfig.payload?.sql || '';
    }
    return '';
  };
  
  // Helper: Get SQL connection key from implementationConfig
  const getSQLConnectionKey = (item: any): string => {
    if (item.implementationConfig?.implementationProviderKey === 'sql') {
      return item.implementationConfig.payload?.connectionKey || '';
    }
    return '';
  };

  // Helper: Update SQL in implementationConfig
  const updateSQLValue = (item: any, sqlValue: string, connectionKey?: string) => {
    const currentConnectionKey = connectionKey !== undefined ? connectionKey : getSQLConnectionKey(item);
    return {
      ...item,
      implementationConfig: {
        implementationProviderKey: 'sql',
        payload: {
          sql: sqlValue,
          connectionKey: currentConnectionKey
        }
      },
      executionProviderKey: 'sql'
    };
  };
  
  // Helper: Update SQL connection
  const updateSQLConnection = (item: any, connectionKey: string) => {
    const currentSQL = getSQLValue(item);
    return updateSQLValue(item, currentSQL, connectionKey);
  };

  // Helper: Get TypeScript from implementationConfig
  const getTypeScriptValue = (item: any): string => {
    if (item.implementationConfig?.implementationProviderKey === 'typescript') {
      return item.implementationConfig.payload?.typescript || '';
    }
    return '';
  };

  // Helper: Update TypeScript in implementationConfig
  const updateTypeScriptValue = (item: any, typescriptValue: string) => {
    return {
      ...item,
      implementationConfig: {
        implementationProviderKey: 'typescript',
        payload: {
          typescript: typescriptValue
        }
      },
      executionProviderKey: 'typescript'
    };
  };

  const handleSourceTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSourceTab(newValue);

    let newItem = { ...updatedItem };

    if (newValue === 0) {
      // User switched *to* Integration tab
      if (newItem.integrationConfig?.integrationFunctionId) {
        // Reinstate Integration as the implementation type
        newItem = {
          ...newItem,
          executionProviderKey: "integration",
          webhookUrl: "",
          mock: "",
        };
      }
    } else if (newValue === 1) {
      // Webhook tab
      newItem = {
        ...newItem,
        executionProviderKey: "webhook",
        integrationConfig: null,
        mock: "",
        implementationConfig: {
          implementationProviderKey: "webhook",
          payload: {
            url: newItem.webhookUrl || ""
          }
        }
      };
    } else if (newValue === 2) {
      // Mock tab
      newItem = {
        ...newItem,
        executionProviderKey: "mock",
        integrationConfig: null,
        webhookUrl: "",
        implementationConfig: {
          implementationProviderKey: "mock",
          payload: {
            mockData: newItem.mock || ""
          }
        }
      };
    } else if (newValue === 3) {
      // SQL tab - use new implementationConfig structure
      const currentSQL = getSQLValue(newItem);
      const currentConnectionKey = getSQLConnectionKey(newItem);
      newItem = {
        ...newItem,
        executionProviderKey: "sql",
        integrationConfig: null,
        webhookUrl: "",
        mock: "",
        implementationConfig: {
          implementationProviderKey: "sql",
          payload: {
            sql: currentSQL,
            connectionKey: currentConnectionKey
          }
        }
      };
    } else if (newValue === 4) {
      // App Configuration tab
      newItem = {
        ...newItem,
        executionProviderKey: "appConfiguration",
        integrationConfig: null,
        webhookUrl: "",
        mock: "",
      };
    } else if (newValue === 5) {
      // TypeScript tab - use new implementationConfig structure
      const currentTypeScript = getTypeScriptValue(newItem);
      
      // If no TypeScript code exists, provide a default example
      const typescriptCode = currentTypeScript || `// TypeScript function implementation
// This function will be executed at runtime with access to repositories

export async function execute(args: any, context: any, getRepository: any, getRepositories: any) {
  try {
    // Example: Get repositories for different entities
    const reservationRepo = await getRepository('Reservation');
    const consumerRepo = await getRepository('Consumer');
    
    if (!reservationRepo) {
      throw new Error('Reservation repository not available');
    }
    
    // Example: Create or find a consumer first
    let consumer;
    if (args.email && consumerRepo) {
      const existingConsumers = await consumerRepo.getManyBy({ email: args.email });
      if (existingConsumers.length > 0) {
        consumer = existingConsumers[0];
      } else {
        // Create new consumer
        consumer = await consumerRepo.create({
          firstName: args.name?.split(' ')[0] || 'Unknown',
          lastName: args.name?.split(' ').slice(1).join(' ') || 'Customer',
          email: args.email,
          phone: args.phone,
          status: 'active'
        });
      }
    }
    
    // Create a new reservation
    const newReservation = {
      name: args.name || 'John Doe',
      phone: args.phone || '+1234567890',
      email: args.email || 'john@example.com',
      date: args.date || new Date().toISOString().split('T')[0],
      time: args.time || '19:00',
      partySize: args.partySize || 2,
      status: 'pending',
      source: 'online',
      notes: args.notes || '',
      specialRequests: args.specialRequests || []
    };
    
    // Save the reservation
    const savedReservation = await reservationRepo.create(newReservation);
    
    // Example: Get all reservations for today
    const todayReservations = await reservationRepo.getManyBy({ 
      date: new Date().toISOString().split('T')[0] 
    });
    
    // Return the result with additional context
    return {
      success: true,
      data: {
        reservation: savedReservation,
        consumer: consumer,
        todayReservationsCount: todayReservations.length
      },
      message: 'Reservation created successfully'
    };
    
  } catch (error) {
    console.error('TypeScript function error:', error);
    return {
      success: false,
      error: error.message,
      message: 'Failed to execute function'
    };
  }
}`;
      
      newItem = {
        ...newItem,
        executionProviderKey: "typescript",
        integrationConfig: null,
        webhookUrl: "",
        mock: "",
        implementationConfig: {
          implementationProviderKey: "typescript",
          payload: {
            typescript: typescriptCode
          }
        }
      };
    }

    setUpdatedItem(newItem);
    onChange(newItem);
  };



  return (
    <Box sx={{ padding: "16px" }}>
      <Typography variant="h6" gutterBottom>
        Function
      </Typography>
      <TextField
        fullWidth
        label="Name"
        value={updatedItem.name}
        onChange={(e) => handleChange("name", e.target.value)}
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        multiline
        label="Description"
        value={updatedItem.description}
        onChange={(e) => handleChange("description", e.target.value)}
        sx={{ mb: 3 }}
      />

      <TextField
        fullWidth
        label="Method"
        value={updatedItem.method}
        onChange={(e) => handleChange("method", e.target.value)}
        sx={{ mb: 4 }}
      />

      <TextField
        fullWidth
        label="Path"
        value={updatedItem.path}
        onChange={(e) => handleChange("path", e.target.value)}
        sx={{ mb: 5 }}
      />

      <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
        <Checkbox
          id="requires-auth"
          checked={updatedItem.requiresAuthentication}
          onChange={(e) => handleChange("requiresAuthentication", e.target.checked)}
          sx={{ mr: 1 }} // Add right margin instead
        />
        <label htmlFor="requires-auth" style={{ marginLeft: 4, fontSize: 16 }}>
          Requires Authentication
        </label>
      </Box>



      <Tabs value={topLevelTab} onChange={handleTopLevelTabChange} sx={{ mb: 2 }}>
        <Tab label="OpenAPI Spec" />
        <Tab 
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Implementation
              {getActiveImplementationType() && (
                <Chip 
                  label={getActiveImplementationType()?.toUpperCase()} 
                  size="small" 
                  color="success"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Box>
          } 
        />
      </Tabs>

      {/* OpenAPI Spec Tab */}
      <TabPanel value={topLevelTab} index={0}>
        <Typography variant="body1" gutterBottom>
          Open API Spec
        </Typography>
        <JsonEditorAce
          value={updatedItem.openApiSpec}
          onChange={(value) =>
            handleChange("openApiSpec", JSON.stringify(value))
          }
        />
      </TabPanel>

      {/* Source Tab */}
      <TabPanel value={topLevelTab} index={1}>
        <Tabs
          value={sourceTab}
          onChange={handleSourceTabChange}
          aria-label="Source Type Tabs"
          sx={{ mb: 2 }}
        >
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Integration
                {isImplementationConfigured('integration') && (
                  <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                )}
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Webhook
                {isImplementationConfigured('webhook') && (
                  <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                )}
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Mock
                {isImplementationConfigured('mock') && (
                  <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                )}
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                SQL
                {isImplementationConfigured('sql') && (
                  <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                )}
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                App Configuration
                {isImplementationConfigured('appConfiguration') && (
                  <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                )}
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                TypeScript
                {isImplementationConfigured('typescript') && (
                  <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                )}
              </Box>
            } 
          />
        </Tabs>

        <TabPanel value={sourceTab} index={0}>
          <Typography variant="h6">Choose Integration</Typography>
          <TreeView
            defaultCollapseIcon={<ExpandMore />}
            defaultExpandIcon={<ChevronRight />}
            expanded={expandedNodes}
            selected={selectedNode} // Always defined
            onNodeSelect={(event, nodeId) => setSelectedNode(nodeId)}
            onNodeToggle={handleNodeToggle}
          >
            {integrationOptions.map((integration) => (
              <TreeItem
                key={integration.integrationId}
                nodeId={integration.integrationId}
                label={integration.name}
              >
                {integration.functions.map((func: any) => (
                  <TreeItem
                    key={func.id}
                    nodeId={func.id}
                    label={func.name}
                    onClick={() =>
                      handleFunctionSelect({
                        integrationId: integration.integrationId,
                        integrationFunctionId: func.id,
                        webhookUrl: func.webhookUrl,
                      })
                    }
                  />
                ))}
              </TreeItem>
            ))}
          </TreeView>
          <Button variant="contained" color="primary" style={{ marginTop: '16px' }}
            onClick={() => handleClear()}>Clear</Button>
        </TabPanel>

        <TabPanel value={sourceTab} index={1}>
          <Typography variant="h6">Webhook</Typography>
          <TextField
            fullWidth
            multiline
            label="Webhook URL"
            value={updatedItem?.webhookUrl}
            onChange={(e) => handleChange("webhookUrl", e.target.value)}
          />
        </TabPanel>

        <TabPanel value={sourceTab} index={2}>
          <Typography variant="h6">Mock Response</Typography>
          <JsonEditorAce
            value={updatedItem.mock}
            onChange={(value) =>
              handleChange("mock", JSON.stringify(value))
            }
          />
        </TabPanel>

        <TabPanel value={sourceTab} index={3}>
          <Typography variant="h6" sx={{ mb: 2 }}>SQL Query</Typography>
          
          {/* Connection Selector */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Database Connection *
            </Typography>
            <Select
              fullWidth
              value={getSQLConnectionKey(updatedItem)}
              onChange={(e) => {
                const updated = updateSQLConnection(updatedItem, e.target.value);
                setUpdatedItem(updated);
                onChange(updated);
              }}
              displayEmpty
              size="small"
            >
              <MenuItem value="" disabled>
                <em>Select a database connection...</em>
              </MenuItem>
              {sqlConnections.map((conn) => (
                <MenuItem key={conn.id} value={conn.key}>
                  {conn.name} ({conn.providerKey})
                </MenuItem>
              ))}
            </Select>
            {sqlConnections.length === 0 && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                No SQL connections found. Please create a Snowflake connection first.
              </Typography>
            )}
          </Box>
          
          {/* SQL Editor */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Use <code>{`{{variableName}}`}</code> syntax to insert context variables (tenant attributes, tenantId, brandId, etc.)
          </Typography>
          <Box sx={{ height: '500px', width: '100%' }}>
            <TemplateEditorAce
              value={getSQLValue(updatedItem)}
              mode="sql"
              onChange={(value) => {
                const updated = updateSQLValue(updatedItem, value);
                setUpdatedItem(updated);
                onChange(updated);
              }}
            />
          </Box>
        </TabPanel>

        <TabPanel value={sourceTab} index={4}>
          <Typography variant="h6">App Configuration</Typography>
          <JsonEditorAce
            value={updatedItem.mock}
            onChange={(value) =>
              handleChange("appConfiguration", JSON.stringify(value))
            }
          />
        </TabPanel>

        <TabPanel value={sourceTab} index={5}>
          <Typography variant="h6" sx={{ mb: 2 }}>TypeScript Implementation</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Write TypeScript code that will be executed at runtime. You have access to:
            <br />• <code>getRepository&lt;T&gt;(entityName: string)</code> - Get repository for any entity (e.g., 'Reservation', 'Consumer', 'Order')
            <br />• <code>getRepositories()</code> - Get all available repositories as a map
            <br />• <code>context</code> - Current execution context with tenant, brand, user info
            <br />• <code>args</code> - Function arguments passed from the API call
            <br />
            <br />Available entities: Reservation, Consumer, Employee, Company, User, Product, Order, Invoice, Payment, Transaction, Location, Inventory, Device, Event, Case
            <br />
            <br />Repository methods: <code>create()</code>, <code>findAll()</code>, <code>get(id)</code>, <code>update(id, data)</code>, <code>delete(id)</code>, <code>search(query)</code>
          </Typography>
          <Box sx={{ height: '600px', width: '100%' }}>
            <TemplateEditorAce
              value={getTypeScriptValue(updatedItem)}
              mode="typescript"
              onChange={(value) => {
                const updated = updateTypeScriptValue(updatedItem, value);
                setUpdatedItem(updated);
                onChange(updated);
              }}
            />
          </Box>
        </TabPanel>
      </TabPanel>
    </Box>
  );
};

export default AIFunctionEdit;
